/**
 * Where the roof faces' labels go on a plan (the Permit workspace's site plan; the PDF's PV-2 does the same,
 * app/design/permits/labels.py): at each face's most open point (farthest from its edges and holes), largest face
 * first, clear of the labels already placed; a label that fits nowhere inside its face goes just outside it with a
 * leader line. Without this a dormer's faces and its host face printed their labels over each other. Units are the
 * plan's (metres); `size` is the text height, a character about 0.62 of it wide.
 */
type P = [number, number];

export interface LabelSpot {
  id: string;
  at: P;
  /** where the leader line starts (inside the face) when the label had to go outside it */
  leader: P | null;
}

function inside([x, y]: P, ring: P[]): boolean {
  let hit = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]!;
    const [xj, yj] = ring[j]!;
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi || 1e-12) + xi) hit = !hit;
  }
  return hit;
}

function segDist(p: P, a: P, b: P): number {
  const dx = b[0] - a[0], dy = b[1] - a[1];
  const t = dx === 0 && dy === 0 ? 0 : Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / (dx * dx + dy * dy)));
  return Math.hypot(p[0] - a[0] - t * dx, p[1] - a[1] - t * dy);
}

/** Points inside the face (not in a hole), most open first. */
export function openPoints(ring: P[], holes: P[][] = [], samples = 24): P[] {
  if (ring.length < 3) return [];
  const xs = ring.map((p) => p[0]), ys = ring.map((p) => p[1]);
  const step = Math.max(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys)) / samples || 1;
  const rings = [ring, ...holes.filter((h) => h.length >= 3)];
  const out: { c: number; p: P }[] = [];
  for (let y = Math.min(...ys) + step / 2; y < Math.max(...ys); y += step)
    for (let x = Math.min(...xs) + step / 2; x < Math.max(...xs); x += step) {
      const p: P = [x, y];
      if (!inside(p, ring) || holes.some((h) => h.length >= 3 && inside(p, h))) continue;
      let c = Infinity;
      for (const r of rings) for (let i = 0; i < r.length; i++) c = Math.min(c, segDist(p, r[i]!, r[(i + 1) % r.length]!));
      out.push({ c, p });
    }
  return out.sort((a, b) => b.c - a.c).map((x) => x.p);
}

const area = (r: P[]) => Math.abs(r.reduce((s, p, i) => s + p[0] * r[(i + 1) % r.length]![1] - r[(i + 1) % r.length]![0] * p[1], 0)) / 2;

/** Each face's label spot. */
export function labelSpots(faces: { id: string; label: string; pts: P[]; holes?: P[][] }[], size: number): LabelSpot[] {
  type Box = [number, number, number, number];
  const taken: Box[] = [];
  const boxAt = (p: P, label: string): Box => {
    const w = label.length * size * 0.62 + size * 0.4;
    return [p[0] - w / 2, p[1] - size * 0.6, p[0] + w / 2, p[1] + size * 0.6];
  };
  const hits = (b: Box) => taken.some((t) => b[0] < t[2] && t[0] < b[2] && b[1] < t[3] && t[1] < b[3]);
  const within = (b: Box, f: { pts: P[]; holes?: P[][] }) =>
    ([[b[0], b[1]], [b[2], b[1]], [b[2], b[3]], [b[0], b[3]]] as P[]).every((c) => inside(c, f.pts) && !(f.holes ?? []).some((h) => h.length >= 3 && inside(c, h)));
  const out: LabelSpot[] = [];
  for (const f of [...faces].sort((a, b) => area(b.pts) - area(a.pts))) {
    const points = openPoints(f.pts, f.holes).slice(0, 40);
    if (!points.length) continue;
    let spot: LabelSpot | null = null;
    for (const p of points) {
      const b = boxAt(p, f.label);
      if (within(b, f) && !hits(b)) {
        spot = { id: f.id, at: p, leader: null };
        taken.push(b);
        break;
      }
    }
    if (!spot) {
      const anchor = points[0]!;
      for (const r of [2, 3, 4.5, 6, 8].map((k) => k * size)) {
        for (let k = 0; k < 16 && !spot; k++) {
          const a = Math.PI / 2 + (k * Math.PI) / 8;
          const p: P = [anchor[0] + r * Math.cos(a), anchor[1] + r * Math.sin(a)];
          const b = boxAt(p, f.label);
          if (!hits(b) && !within(b, f)) {
            spot = { id: f.id, at: p, leader: anchor };
            taken.push(b);
          }
        }
        if (spot) break;
      }
      if (!spot) {
        spot = { id: f.id, at: anchor, leader: null };
        taken.push(boxAt(anchor, f.label));
      }
    }
    out.push(spot);
  }
  return out;
}
